import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactComponent as AddCircleOutlineOutlinedIcon } from '../../../assets/SVG/whiteAddSquare.svg';
import { ReactComponent as SearchIcon } from '../../../assets/SVG/Search-Icon.svg';
import { ReactComponent as Filter } from '../../../assets/SVG/filter.svg';
import CustomPagination from '../../../components/common/CustomPagination';
import InventoryApprovalTable from './InventoryApprovalTable';
import InventoryApprovalFilterModal from './InventoryApprovalFilterModal';
import {
  ApprovalFilterValues,
  APPROVAL_FILTER_INITIAL_VALUES,
} from './inventoryApproval.types';
import { activeBadgeSx } from '../../Inventory/Common/inventory.helper';
import {
  FilterIconSx,
  approvalListContainerSx,
  approvalListHeaderSx,
  approvalListTitleSx,
  approvalListActionsSx,
  approvalListSearchSx,
  approvalListCreateBtnSx,
} from './InventoryApprovalSx';
import { useApprovalRules, useToggleApprovalRule } from './inventoryApproval.queries';
import { useDebounce } from '../../Inventory/hooks/useDebounce';
import { useAlert } from '../../../components/alertProvider';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE_OPTIONS = [9, 15, 20, 25];

const InventoryApprovalList: React.FC = () => {
  const navigate = useNavigate();
  const { t: tInventoryApproval } = useTranslation('inventory_approval_config');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState<ApprovalFilterValues>(APPROVAL_FILTER_INITIAL_VALUES);

  const queryParams = useMemo(() => ({
    search: debouncedSearch.trim() || undefined,
    clinic: activeFilters.clinicId ?? undefined,
    document_type: activeFilters.documentType ?? undefined,
    status: activeFilters.status ? activeFilters.status : undefined,
    limit: pageSize,
    offset: page * pageSize,
  }), [debouncedSearch, activeFilters, page, pageSize]);

  const { data: apiResponse, isLoading, isError } = useApprovalRules(queryParams);
  const { mutate: toggleRule } = useToggleApprovalRule();
  const { addAlert } = useAlert();

  const approvalRules = useMemo(() => apiResponse?.objects ?? [], [apiResponse?.objects]);
  const totalCount = apiResponse?.meta?.total_count ?? 0;

  const [activeStates, setActiveStates] = useState<Record<number, boolean>>({});

  // Sync activeStates when API data arrives
  React.useEffect(() => {
    if (approvalRules.length > 0) {
      setActiveStates((prev) => {
        const map: Record<number, boolean> = { ...prev };
        approvalRules.forEach((r) => {
          map[r.id] = r.status === 'ACTIVE';
        });
        return map;
      });
    }
  }, [approvalRules]);

  const handleToggle = (id: number) => {
    const previous = activeStates[id] ?? false;
    const nextActive = !previous;
    // Optimistic update
    setActiveStates((prev) => ({ ...prev, [id]: nextActive }));
    toggleRule(id, {
      onSuccess: () => {
        addAlert(
          nextActive ? tInventoryApproval('rule_activated') : tInventoryApproval('rule_deactivated'),
          'success'
        );
      },
      onError: () => {
        // Rollback on failure
        setActiveStates((prev) => ({ ...prev, [id]: previous }));
        addAlert(tInventoryApproval('toggle_error'), 'error');
      },
    });
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => setPage(newPage);
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(0);
  };

  return (
    <Box sx={approvalListContainerSx}>
      <Box sx={approvalListHeaderSx}>
        <Typography variant="h6" sx={approvalListTitleSx}>
          {tInventoryApproval('title')} ({isLoading ? '...' : totalCount})
        </Typography>

        <Box sx={approvalListActionsSx}>
          <TextField
            placeholder={tInventoryApproval('search_placeholder')}
            size="small"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={approvalListSearchSx}
            inputProps={{ 'data-testid': 'inventory-approval-search-input' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <Tooltip title={tInventoryApproval('filter')} arrow>
            <IconButton
              data-testid="inventory-approval-filter-btn"
              onClick={() => setFilterOpen(true)}
            >
              <Badge
                variant="dot"
                color="error"
                invisible={
                  !activeFilters.documentType &&
                  !activeFilters.clinicId &&
                  !activeFilters.status
                }
                sx={activeBadgeSx}
              >
                <Filter style={FilterIconSx} />
              </Badge>
            </IconButton>
          </Tooltip>



          <Button
            variant="contained"
            startIcon={<AddCircleOutlineOutlinedIcon />}
            sx={approvalListCreateBtnSx}
            onClick={() => navigate('create')}
            data-testid="inventory-approval-create-btn"
          >
            {tInventoryApproval('create_new_rule')}
          </Button>
        </Box>
      </Box>

      <InventoryApprovalFilterModal
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={(values) => {
          setActiveFilters(values);
          setPage(0);
        }}
        onClear={() => {
          setActiveFilters(APPROVAL_FILTER_INITIAL_VALUES);
          setPage(0);
        }}
        initialValues={activeFilters}
      />

      <InventoryApprovalTable
        rows={approvalRules}
        activeStates={activeStates}
        onToggle={handleToggle}
        onEdit={(id) => navigate(`${id}/edit`)}
        onView={(id) => navigate(`${id}/view`)}
        isLoading={isLoading}
        isError={isError}
      />

      <CustomPagination
        page={page}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        totalCount={totalCount}
        onPageChange={handlePageChange}
      />
    </Box>
  );
};

export default InventoryApprovalList;
